import { useState } from 'react';
import { Phone } from '@phosphor-icons/react';
import { Button } from 'src/components/Button';
import { Input } from 'src/components/Input';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Agent } from 'src/interfaces/agent.interface';
import { AnimatedCircles } from './AnimatedCircles';

interface PhoneAgentCallerProps {
  agent: Agent;
  fromNumber: string;
  onContinue: () => void;
  onCallCreated: () => void;
  onWebCall: () => void;
}

export const PhoneAgentCaller = ({
  agent,
  fromNumber,
  onContinue,
  onCallCreated,
  onWebCall,
}: PhoneAgentCallerProps) => {
  const { post } = useAuthedRequest();
  const notification = useNotification();

  const [phoneNumber, setPhoneNumber] = useState('');
  const [isCreatingCall, setIsCreatingCall] = useState(false);

  const handleCreateCall = async () => {
    if (!phoneNumber || phoneNumber.trim().length === 0) {
      notification.error('Please provide your phone number');
      return;
    }

    const data = {
      from_number: fromNumber,
      to_number: phoneNumber,
      agent: agent.id,
      on_no_human_answer: 'continue' as const,
      run_do_not_call_detection: false,
      hipaa_compliant: false,
      context: {},
      telephony_params: {},
    };

    try {
      setIsCreatingCall(true);
      await post('/calls', data);
      notification.success('Calling you now...');
      onCallCreated();
    } catch (error) {
      const err = error as { status?: number; data?: { message?: string } };
      const errorMessage = err.data?.message;

      if (
        err?.status === 429 &&
        errorMessage &&
        errorMessage.includes('Concurrent call limit')
      ) {
        notification.warning(
          "You have reached the account's concurrent call limit. Please upgrade your plan to increase your limit.",
        );
      } else if (
        err?.status === 429 &&
        errorMessage &&
        !errorMessage.includes('Concurrent call limit')
      ) {
        notification.warning(errorMessage);
      } else if (errorMessage) {
        notification.error(errorMessage);
      } else {
        notification.error(
          'Failed to start call: ' +
            (error instanceof Error ? error.message : 'Unknown error'),
        );
      }
    } finally {
      setIsCreatingCall(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center w-full min-h-[60vh]">
      <div className="container mx-auto flex-1 mt-12">
        <div className="flex flex-col items-center justify-center p-8 m-2">
          <AnimatedCircles
            isMaximized
            isCalling={false}
            animate
            variant="large"
          />

          <p className="text-2xl text-neutral-0 mt-6 mb-6 text-center">
            Receive a live call from our agent
          </p>

          <div className="flex flex-col gap-4 w-full max-w-md">
            <Input
              id="phone-number"
              label="Phone Number"
              placeholder="Enter your phone number"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              disabled={isCreatingCall}
              variant="outlined"
              color="primary"
              required
              className="bg-neutral-0 border-0 text-neutral-600"
              labelClassName="!text-neutral-0 required-field-white"
            />

            <div className="flex flex-col gap-3 w-full">
              <Button
                onClick={handleCreateCall}
                disabled={isCreatingCall}
                className="w-full gap-2"
              >
                {isCreatingCall ? (
                  'Placing call...'
                ) : (
                  <>
                    <Phone size={20} />
                    Get a Call
                  </>
                )}
              </Button>

              <Button
                variant="outlined"
                onClick={onContinue}
                className="w-full bg-neutral-0"
                disabled={isCreatingCall}
              >
                Continue Onboarding
              </Button>

              <Button
                variant="text"
                className="text-neutral-0 underline px-0 self-center"
                onClick={onWebCall}
              >
                Get a call in your browser instead
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
